import { useEffect, useMemo, useState, useCallback  } from "react";
import axios from "axios";
import { order, backend } from "../../components/apipath";
import { dateHelper } from "../../modules/helpers";
import "./style/admorder.scss";
import Swal from "sweetalert2";

const PARENT_LABEL_MAP = {
  1: "CPA",
  2: "CMA",
  3: "EA",
  6: "美稅",
};

function getParentLabel(parent_id) {
  const id = Number(parent_id);
  return PARENT_LABEL_MAP[id];
}

const PRODUCT_CATEGORY_MAP = {
  CPA: [1, 2, 3, 4, 5],
  CMA: [6, 7],
  EA: [8, 9, 26, 27],
  學分班: [10],
  美稅: [12, 13, 14, 15, 16, 17, 23, 24],
  CPE:[11, 18],
  舊生:[19, 20, 21],
  CIA:[22],
};

function getCategoryByProductId(productId) {
  const id = Number(productId);
  if (!id) return "";

  for (const [label, ids] of Object.entries(PRODUCT_CATEGORY_MAP)) {
    if (ids.includes(id)) return label;
  }
  return ""; // 找不到就給一個預設
}

function OrderList() {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [keyword, setKeyword] = useState("");
    const limit = 50;

    // ===== 新增：產品清單與多選篩選 =====
    const [products, setProducts] = useState([]);
    const [prodOpen, setProdOpen] = useState(false);
    const [prodSearch, setProdSearch] = useState("");
    const [productIds, setProductIds] = useState([]); // 多選後要帶到後端的 product_ids

    const filteredProducts = useMemo(() => {
        const kw = prodSearch.trim().toLowerCase();
        if (!kw) return products;
        return products.filter(
        (p) =>
            String(p.product_id).includes(kw) ||
            (p.name || "").toLowerCase().includes(kw)
        );
    }, [products, prodSearch]);

    const allChecked = useMemo(() => {
        return products.length > 0 && productIds.length === products.length;
    }, [products, productIds]);

    const toggleAllProducts = (checked) => {
    const next = checked ? products.map(p => String(p.product_id)) : [];
    setProductIds(next);
    setPage(1);
    fetchOrders(1, next);           // ← 立刻查
    };

    const toggleOneProduct = (id, checked) => {
    const set = new Set(productIds.map(String));
    if (checked) set.add(String(id));
    else set.delete(String(id));
    const next = Array.from(set);
    setProductIds(next);
    setPage(1);
    fetchOrders(1, next);           // ← 立刻查
    };


    // ===== 取得產品清單 =====
    useEffect(() => {
        (async () => {
        try {
            const { data } = await axios.get(`${backend}product`);
            setProducts(Array.isArray(data) ? data : data?.data || []);
        } catch (err) {
            console.error("取得產品清單失敗：", err);
        }
        })();
    }, []);

    // ===== 取得訂單列表 =====
    const fetchOrders = async (customPage = page, ids = productIds) => {
    setLoading(true);
    try {
        const resp = await axios.get(`${order}list`, {
        params: {
            page: customPage,
            limit,
            keyword,
            product_ids: ids,            // 方式 A：陣列（product_ids[]=1&product_ids[]=2）
            product_ids_csv: ids.join(','), // 方式 B：逗號字串（1,2,3）
        },
        paramsSerializer: {
            serialize: (params) => {
            const esc = encodeURIComponent;
            const arr = [];
            for (const k in params) {
                if (Array.isArray(params[k])) {
                params[k].forEach((v) => arr.push(`${esc(k)}[]=${esc(v)}`));
                } else if (params[k] !== "" && params[k] != null) {
                arr.push(`${esc(k)}=${esc(params[k])}`);
                }
            }
            return arr.join("&");
            },
        },
        });

        setOrders(resp.data.orders || resp.data.data || []);
        setTotal(resp.data.total || 0);
    } catch (error) {
        console.error("載入訂單資料失敗：", error);
    } finally {
        setLoading(false);
    }
    };


    // 換頁時自動載入
    useEffect(() => {
        fetchOrders();
    }, [page]);

    // 搜尋按鈕：回到第 1 頁並查詢
    const handleSearch = () => {
        setPage(1);
        fetchOrders(1);
    };

    const getCouponLabel = (o) => {
        if (o?.discount_code) return `Discount - ${o.discount_code}`;
        if (o?.mou_code) return `MOU - ${o.mou_code}`;
        return "—";
    };

    const handleDeleteOrder = async (orderId) => {
        const result = await Swal.fire({
        title: "確定要刪除這筆訂單嗎？",
        text: `訂單編號：${orderId}`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "是，刪除",
        cancelButtonText: "取消",
        confirmButtonColor: "#d33",
        });

        if (!result.isConfirmed) return;

        try {
        // 假設後端 route 是 DELETE /order/:id
        await axios.delete(`${backend}order/${orderId}`);

        Swal.fire({
            icon: "success",
            title: "已刪除",
            text: "訂單已成功刪除。",
        });

        // 刪除後重新載入當前頁
        fetchOrders();
        } catch (err) {
        console.error("刪除訂單失敗：", err);
        Swal.fire({
            icon: "error",
            title: "刪除失敗",
            text: "刪除訂單時發生錯誤，請稍後再試。",
        });
        }
    };

    return (
        <div className="container-fluid p-5 mt100">
        <h2>訂單管理</h2>

        {/* 篩選列 */}
        <div className="panel panel-default">
            <div className="panel-body">
            <div className="form-inline clearfix">
                <div className="pull-right">
                    {/* 關鍵字 */}
                    <div className="form-group" style={{ marginRight: 10 }}>
                        <input
                            type="text"
                            className="form-control mb-5"
                            placeholder="搜尋姓名、產品、訂單編號"
                            value={keyword}
                            onChange={(e) => setKeyword(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                            style={{ minWidth: 260 }}
                        />
                    </div>

                    {/* 產品多選（彈層） */}
                    <div className="form-group" style={{ position: "relative", marginRight: 10 }}>
                        <button
                        type="button"
                        className="btn btn-default"
                        onClick={() => setProdOpen(v => !v)}
                        style={{
                            position: "absolute",
                            top: -50,
                            right: 0
                        }}
                        >
                            <span>產品篩選（{productIds.length}）</span>
                            <span className={`caret ${prodOpen ? 'dropup' : ''}`} />
                        </button>

                        {prodOpen && (
                            <div
                            className="panel panel-default filter-popover"
                            style={{
                                position: "absolute",
                                zIndex: 1050,
                                top: "110%",
                                right: 0,
                                left: "auto",
                                width: 360,
                                maxHeight: 360,
                                overflow: "hidden"
                            }}
                            >
                        <div className="panel-heading" style={{ padding: "8px 10px" }}>
                            <div className="input-group input-group-sm">
                            <input
                                type="text"
                                className="form-control"
                                placeholder="搜尋產品名稱或ID"
                                value={prodSearch}
                                onChange={(e) => setProdSearch(e.target.value)}
                            />
                            <span className="input-group-btn">
                                <button
                                className="btn btn-default"
                                onClick={() => setProdSearch("")}
                                type="button"
                                >
                                清除
                                </button>
                            </span>
                            </div>
                            <div className="m-t-5">
                            <label className="checkbox-inline" style={{ userSelect: "none" }}>
                                <input
                                type="checkbox"
                                checked={allChecked}
                                onChange={(e) => toggleAllProducts(e.target.checked)}
                                />{" "}
                                全選/清空
                            </label>
                            </div>
                        </div>

                        <div className="panel-body" style={{ padding: 0, maxHeight: 230, overflow: "auto" }}>
                            <ul className="list-group" style={{ marginBottom: 0 }}>
                            {filteredProducts.map((p) => {
                                const id = String(p.product_id);
                                const checked = productIds.map(String).includes(id);
                                return (
                                <li key={id} className="list-group-item" style={{ padding: "6px 10px" }}>
                                    <label style={{ fontWeight: "normal", margin: 0, userSelect: "none" }}>
                                    <input
                                        type="checkbox"
                                        className="m-r-5"
                                        checked={checked}
                                        onChange={(e) => toggleOneProduct(id, e.target.checked)}
                                    />
                                    [{getParentLabel(p.parent_id)}] {p.name}
                                    </label>
                                </li>
                                );
                            })}
                            {filteredProducts.length === 0 && (
                                <li className="list-group-item text-muted">無符合產品</li>
                            )}
                            </ul>
                        </div>

                        <div className="panel-footer text-right" style={{ padding: "8px 10px" }}>
                            <button className="btn btn-default btn-sm m-r-5" onClick={() => setProdOpen(false)}>
                            關閉
                            </button>
                            <button
                            className="btn btn-primary btn-sm"
                            onClick={() => {
                                setProdOpen(false);
                                handleSearch();
                            }}
                            >
                            套用
                            </button>
                        </div>
                        </div>
                    )}
                    </div>
                    <button className="btn btn-primary mb-3" onClick={handleSearch}>
                    搜尋
                    </button>
                </div>
            </div>
            </div>
        </div>

        {/* 資料表 */}
        {loading ? (
            <p>資料載入中...</p>
        ) : (
            <>
            <div className="table-responsive">
                <table className="table table-striped border">
                <thead>
                    <tr>
                    <th>訂單編號</th>
                    <th>學員</th>
                    <th>產品 / 詳細</th>
                    <th>優惠碼</th>
                    <th>價格</th>
                    <th>公司</th>
                    <th>匯款人</th>
                    <th>匯款日</th>
                    <th>下單日</th>
                    <th>操作</th>
                    </tr>
                </thead>
                <tbody>
                    {orders.map((o) => (
                  <OrderRow
                    key={o.order_id}
                    row={o}
                    onDelete={handleDeleteOrder}
                  />
                ))}
                </tbody>
                </table>
            </div>

            <div className="mt-3 text-center">
                <button
                className="btn btn-outline-secondary me-2"
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
                >
                上一頁
                </button>
                <span>
                第 {page} 頁 / 共 {Math.ceil((total || 0) / limit)} 頁
                </span>
                <button
                className="btn btn-outline-secondary ms-2"
                disabled={page >= Math.ceil((total || 0) / limit)}
                onClick={() => setPage(page + 1)}
                >
                下一頁
                </button>
            </div>
            </>
        )}
        </div>
    );
    }

    /** 單列：精簡欄位 + 展開顯示你想隱藏的欄位 */
    function OrderRow({ row, colSpan = 10,onDelete }) {
    const [open, setOpen] = useState(false);
    const toggle = useCallback(() => setOpen(v => !v), []);

    return (
        <>
        {/* 主列：整列可點 */}
        <tr
            className="row-toggle"
            role="button"
            aria-expanded={open}
            tabIndex={0}
            onClick={toggle}
            onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                toggle();
            }
            }}
        >
            <td>
            <span className={`caret caret-rotate ${open ? "open" : ""}`} />
            &nbsp;{row.order_id}
            </td>

            <td>
            {/* 主列內如果有可點連結，避免冒泡打開/關閉 */}
            <a
                href={`/adm/admmemberedit/${row.mem_id}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
            >
                {row.student_name}
            </a>
            {/* <div className="text-muted small">{row.email || "—"}</div>
            <div className="text-muted small">{row.phone || "—"}</div> */}
            </td>

            <td>
                <span className="label label-default" style={{ marginRight: 4 }}>
                    {getCategoryByProductId(row.product_id)}
                </span>
                <div dangerouslySetInnerHTML={{ __html: row.product_name }}></div>
            </td>
            <td>{getCouponLabelInline(row)}</td>
            <td>{fmtCurrency(row.total_price)}</td>
            <td>{row.company || "—"}</td>
            <td>{row.transfer_name || "—"}</td>
            <td>{row.transfer_date ? dateHelper(row.transfer_date, "date") : "—"}</td>
            <td>{row.order_date ? dateHelper(row.order_date, "date") : "—"}</td>
            <td>
            <button
                type="button"
                className="btn btn-danger btn-xs"
                onClick={(e) => {
                e.stopPropagation();
                onDelete && onDelete(row.order_id);
                }}
            >
                刪除
            </button>
            </td>
        </tr>

        {/* 明細列：收合區塊（展開時才渲染） */}
        {open && (
            <tr className="row-details">
            <td colSpan={colSpan}>
                <dl className="dl-horizontal" style={{ marginBottom: 0 }}>
                <dt>統編</dt>
                <dd>{row.tax_num || "—"}</dd>

                <dt>公司</dt>
                <dd>{row.company || "—"}</dd>

                <dt>領書選擇</dt>
                <dd>{fmtDeliver(row.deliver)}</dd>

                <dt>上課方式</dt>
                <dd>{fmtMode(row.course)}</dd>

                <dt>加購科目</dt>
                <dd>{fmtAddons(row.book)}</dd>

                <dt>寄送地址</dt>
                <dd>{row.address || "—"}</dd>

                <dt>EA選購科目</dt>
                <dd>{row.ea_parts || "—"}</dd>
                
                <dt>備註</dt>
                <dd>{row.memo || "—"}</dd>

                </dl>
            </td>
            </tr>
        )}
        </>
    );
    }

    /* 你原本的小工具函式（照舊） */
    function getCouponLabelInline(o) {
    if (o?.discount_code) return `${o.discount_code}`;
    if (o?.mou_code) return `${o?.mou_code}`;
    return "—";
    }
    function fmtCurrency(n) {
    if (n == null) return "—";
    const x = Number(n);
    if (Number.isNaN(x)) return String(n);
    return `NT$${x.toLocaleString("zh-TW")}`;
    }
    function fmtDeliver(v) {
    if (!v) return "—";
    return Array.isArray(v) ? v.join("、") : String(v);
    }
    function fmtMode(v) {
    if (!v) return "—";
    const map = { onsite: "現場面授", online: "線上", hybrid: "混合" };
    return map[v] || String(v);
    }
    function fmtAddons(v) {
    if (!v) return "—";
    return Array.isArray(v) ? v.join("、") : String(v);
    }

    export default OrderList;
